import React from 'react';
import {
  Box,
  Typography,
  makeStyles,
  CircularProgress,
} from '@material-ui/core';
import FolderIcon from '@material-ui/icons/Folder';
import { HomeCard } from './HomeCard';
import { ProjectItem, StatusPill } from './ProjectItem';

const useStyles = makeStyles(theme => ({
  statusPills: {
    display: 'flex',
    gap: 8,
  },
  projectList: {
    display: 'flex',
    flexDirection: 'column',
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: 150,
  },
  emptyState: {
    textAlign: 'center',
    padding: theme.spacing(4),
  },
  emptyIcon: {
    fontSize: 40,
    color: theme.palette.text.disabled,
    marginBottom: theme.spacing(1),
  },
  emptyText: {
    color: theme.palette.text.secondary,
    fontSize: 12,
  },
  emptyAction: {
    color: theme.palette.warning.main,
    fontSize: 12,
    marginTop: theme.spacing(1),
    cursor: 'pointer',
    '&:hover': {
      textDecoration: 'underline',
    },
  },
}));

type ProjectStats = {
  done: number;
  inProgress: number;
  todo: number;
};

type Project = {
  id: string;
  name: string;
  client?: string;
  stats?: ProjectStats;
  updatedAt?: string;
};

type AIAgenticCardProps = {
  projects?: Project[];
  totalStats?: ProjectStats;
  loading?: boolean;
  actionHref?: string;
  onProjectClick?: (project: Project) => void;
  onCreateProject?: () => void;
};

function formatRelativeTime(dateStr: string): string {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffMins < 1) return 'now';
  if (diffMins < 60) return `${diffMins}m`;
  if (diffHours < 24) return `${diffHours}h`;
  if (diffDays < 7) return `${diffDays}d`;
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

export const AIAgenticCard = ({
  projects = [],
  totalStats,
  loading = false,
  actionHref = '/proj-onboard',
  onProjectClick,
  onCreateProject,
}: AIAgenticCardProps) => {
  const classes = useStyles();

  // Calculate total stats from projects if not provided
  const stats =
    totalStats ||
    projects.reduce(
      (acc, p) => ({
        done: acc.done + (p.stats?.done || 0),
        inProgress: acc.inProgress + (p.stats?.inProgress || 0),
        todo: acc.todo + (p.stats?.todo || 0),
      }),
      { done: 0, inProgress: 0, todo: 0 },
    );

  const headerContent = (
    <Box className={classes.statusPills}>
      {stats.done > 0 && (
        <StatusPill count={stats.done} label="done" status="done" />
      )}
      {stats.inProgress > 0 && (
        <StatusPill
          count={stats.inProgress}
          label="in progress"
          status="in_progress"
        />
      )}
      {stats.todo > 0 && (
        <StatusPill count={stats.todo} label="to do" status="todo" />
      )}
    </Box>
  );

  return (
    <HomeCard
      title="AI Agentic Development"
      actionLabel="View Projects"
      actionHref={actionHref}
      headerContent={
        !loading && (stats.done > 0 || stats.inProgress > 0 || stats.todo > 0)
          ? headerContent
          : undefined
      }
    >
      {loading ? (
        <Box className={classes.loadingContainer}>
          <CircularProgress size={24} />
        </Box>
      ) : projects.length > 0 ? (
        <Box className={classes.projectList}>
          {projects.map(project => (
            <ProjectItem
              key={project.id}
              name={project.name}
              client={project.client}
              icon={<FolderIcon style={{ fontSize: 14, color: '#A855F7' }} />}
              stats={project.stats}
              time={
                project.updatedAt
                  ? formatRelativeTime(project.updatedAt)
                  : undefined
              }
              href={`/proj-onboard/projects/${project.id}`}
              onClick={
                onProjectClick ? () => onProjectClick(project) : undefined
              }
            />
          ))}
        </Box>
      ) : (
        <Box className={classes.emptyState}>
          <FolderIcon className={classes.emptyIcon} />
          <Typography className={classes.emptyText}>
            No active projects yet
          </Typography>
          {onCreateProject && (
            <Typography
              className={classes.emptyAction}
              onClick={onCreateProject}
            >
              Create your first project →
            </Typography>
          )}
        </Box>
      )}
    </HomeCard>
  );
};
